import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { UserProfileInfo } from '@app/core/services/bff/user-profile.service';
import { ChatService } from '@app/core/services/chat.service';
import { AccountBookmarkService } from '@app/core/services/lingo2-account/account-bookmark';
import { IStatsList } from '@app/teachers/teacher-card/teacher-card-variables';
import { AccountService, AuthService, FeaturesService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { subscribeToUser, unsubscribeFromUser } from '@store/actions/users.actions';
import { getLanguages, getSubjects } from '@store/reducers/data.reducer';
import { differenceInMinutes } from 'date-fns';
import {
  EntityTypeEnum,
  FeatureEnum,
  ImageSizeEnum,
  Language,
  Subject as BaseSubject,
  Subject,
  SubjectCategoryEnum,
  User,
  UserStatusEnum,
} from 'lingo2-models';
import { first, uniqBy } from 'lodash-es';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/*
 */
@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent extends ChangableComponent implements OnInit {
  private accountService = inject(AccountService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private features = inject(FeaturesService);
  private chatService = inject(ChatService);
  private store = inject(Store);
  private location = inject(Location);
  deviceService = inject(DeviceDetectorService);

  readonly accountBookmarkService = inject(AccountBookmarkService);

  @ViewChild('navPosition') navPosition: ElementRef;
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.deviceService.isMobile() && this.isBrowser) {
      if (!this.isModal) {
        this.handleStickyCheck();
      }
    }
  }
  @Input() public set me(me: User) {
    if (this._me?.id !== me?.id) {
      this._me = me;
      this.init();
    }
  }
  public get me(): User {
    return this._me;
  }
  @Input() public set user(user: User) {
    if (this._user?.id !== user?.id) {
      this._user = user;
      this.init();
      this.prepareUserStats();
    }
  }
  public get user(): User {
    return this._user;
  }
  @Input() public set profile(profile: UserProfileInfo) {
    if (this._profile?.slug !== profile?.slug) {
      this._profile = profile;
      this.init();
      this.prepareUserStats();
    }
  }
  public get profileInfo(): UserProfileInfo {
    return this._profile;
  }

  @Input() public set isModal(isModal: boolean) {
    this._isModal = isModal;
    this.init();
  }

  public get isModal() {
    return this._isModal;
  }

  @Input() selectedSubjectId: string;
  @Output() public isCloseAllModals = new EventEmitter<boolean>();

  public fullName: string;
  public countryFlagUrl: string;
  public countryName: string;
  public lastActiveAt: Date;
  public statusOnline: boolean;
  public profileVerified: boolean;
  public isFavorite: boolean;
  public isSubscribed: boolean;
  public openComplainForm: boolean;
  public spokenLanguages: string[] = [];
  public spokenLanguagesForDisplay: string[] = [];
  public spokenLanguagesOthers: string[] = [];
  public teachingSubjects: BaseSubject[] = [];
  public teachingSubjectsOthers: BaseSubject[] = [];
  public isMe = false;
  public cover: OnUiCover;
  public isTeacher = false;
  public stats: Array<Partial<IStatsList>> = [];
  public statsLine2: Array<Partial<IStatsList>> = [];
  public subjectCategoryEnum = SubjectCategoryEnum;
  public openSpeakLanguagesTooltip: boolean;
  public openTeachingSubjectsTooltip: boolean;
  public avatar: OnUiCover;
  public authorUrl: string;
  private scrollAnimationFrame: number;
  private modalScrollContainer: HTMLElement | null = null;

  public showMoreSubjects = false;
  public showMoreLanguages = false;
  public subjectId: string;
  public disciplineChanged: string;

  public _me: User;
  public _profile: UserProfileInfo;
  public _user: User;
  public _subjects: Subject[];
  private _languages: Language[];
  private _isModal: boolean;
  public openActionsPopup: boolean;
  public activePage: string;
  public isLoaded: boolean;
  public loaded = true;
  public loadedTimer: any;
  public stuckState: boolean;
  public topFromHeader: number;

  public ngOnInit() {
    combineLatest([this.store.select(getSubjects), this.store.select(getLanguages)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this._languages = languages.all;
          this._subjects = subjects;
          this.init();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.subjectId = this.selectedSubjectId
      ? this.selectedSubjectId
      : this.parseSubjectIdFromUrl(this.router.url)
        ? this.parseSubjectIdFromUrl(this.router.url)
        : String(first(this._subjects).id);

    if (this.router.url.split('/')[3] && this.router.url.split('/')[1] == 'u') {
      this.setActivePage(this.router.url.split('/')[3]);
    } else {
      this.setActivePage(this.isTeacher ? 'teaching' : 'about');
    }
    if (this.isModal) {
      this.modalScrollContainer = document.querySelector('app-mobile-modal-body');
      this.modalScrollContainer?.addEventListener('scroll', this.onModalScroll, { passive: true });
    }
  }

  public onModalScroll = () => {
    this.handleStickyCheck();
  };

  private handleStickyCheck() {
    cancelAnimationFrame(this.scrollAnimationFrame);
    this.scrollAnimationFrame = requestAnimationFrame(() => {
      const headerHeight = document.getElementsByClassName('header')[0]?.clientHeight || 0;
      const top = this.navPosition?.nativeElement?.getBoundingClientRect()?.top ?? 0;
      const threshold = 1;

      this.topFromHeader = this.isModal ? 0 : headerHeight;
      const expectedTop = this.isModal ? 60 : headerHeight;

      const isStuckNow = Math.abs(top - expectedTop) < threshold;

      if (this.stuckState !== isStuckNow) {
        this.stuckState = isStuckNow;
        this.detectChanges();
      }
    });
  }

  public toggleMore() {
    if (!this.deviceService.isDesktop()) {
      this.showMoreSubjects = !this.showMoreSubjects;
    }
  }

  public toggleMoreLang() {
    if (!this.deviceService.isDesktop()) {
      this.showMoreLanguages = !this.showMoreLanguages;
    }
  }

  public accountRoute(page: string): string[] {
    return AccountService.accountRoute(this._user, [page]);
  }

  public goEditProfile() {
    if (this.me && this.me.status !== UserStatusEnum.guest) {
      this.editProfileNavigate();
    } else {
      this.authService.showAuthModal(() => this.editProfileNavigate());
    }
  }

  public editProfileNavigate() {
    this.router.navigateByUrl('/profile').then(() => true);
  }

  private prepareUserStats() {
    // const stats: Partial<IAccountStats> = this._user ? this._user.stats || {} : {};

    this.stats = [
      {
        name: 'subscribers',
        title: 'user-profile.stats.subscribers',
        value: this._profile?.numberOfSubscribers || 0,
      },
      {
        name: 'subscriptions',
        title: 'user-profile.stats.subscriptions',
        value: this._profile?.numberOfSubscriptions || 0,
      },
    ];

    if (this.isTeacher) {
      this.stats.unshift({
        name: 'publications',
        title: 'user-profile.teacher-stat.created-lessons',
        value: this._profile?.numberOfLessons || 0,
      });
    }

    this.statsLine2 = [
      {
        name: 'lessons_count',
        title: 'teacher-card.stats.classes',
        value: this._profile?.numberOfMeetings || 0,
      },
      {
        name: 'students_count',
        title: 'teacher-card.stats.students',
        value: this._profile?.numberOfStudents || 0,
      },
    ];
  }

  protected init() {
    if (!this._user || !this._profile || !this._subjects || !this._languages || this.isLoaded) {
      return;
    }

    // Вычисляем все значения за один проход
    const updates = {
      isMe: this._me?.id === this._user?.id,
      isTeacher: AccountService.isAsIfTeacher(this._user),
      isFavorite: this._user?.visit_info?.is_favorite,
      fullName: AccountService.getUserName(this._user),
      activePage: this.isTeacher ? 'teaching' : 'about',
      authorUrl: '/u/' + this._user.slug,
      countryFlagUrl: this._user?.country?.flag_url,
      countryName: this._user?.country?.title,
      lastActiveAt: new Date(this._user?.last_active_at),
      statusOnline: differenceInMinutes(new Date(), new Date(this._user?.last_active_at)) <= 5,
      profileVerified: this._user?.teacher_profile_verified,
    };

    // Применяем все обновления
    Object.assign(this, updates);

    // Обновляем cover и avatar
    this.cover = {
      img:
        FilesService.getFileUrlBySize(this._user?.cover_id, ImageSizeEnum.wide) ||
        this.assetsUrl(
          '/assets/images/headers/profile-banner' + (this.deviceService.isMobile() ? '-mobile' : '') + '.jpg',
        ),
      title: this.fullName,
    };

    this.avatar = {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(this._user.userpic_id),
      title: this.fullName,
      form: 'circle',
    };

    this.spokenLanguages =
      this._profile.spokenLanguages
        ?.filter((sl) => sl.languageId > 0)
        .map((language) => {
          const _language = this._languages.find((l) => l.id == language.languageId);
          return _language ? _language.title : null;
        }) || [];

    this.spokenLanguagesForDisplay = this.spokenLanguages.slice(0, 2);
    this.spokenLanguagesOthers = this.spokenLanguages.slice(2);

    this.teachingSubjects =
      this._profile.teachingSubjects
        ?.filter((ts) => ts.subjectId > 0)
        .map((subject) => {
          const _subject = this._subjects?.find((s) => s.id === +subject.subjectId);
          return _subject
            ? {
                id: _subject.id,
                title: _subject.title,
                category_id: _subject.category_id,
                code: _subject.code,
                language_id: _subject.language_id,
              }
            : null;
        })
        .filter((s) => !!s) || [];

    this.teachingSubjects = uniqBy(this.teachingSubjects, 'id');
    this.teachingSubjectsOthers = this.teachingSubjects.slice(2);

    this.loadIsSubscribed();
    this.isLoaded = true;
  }

  protected loadIsSubscribed() {
    if (!this.isBrowser) {
      return;
    }

    this.accountService
      .getIsSubscribed(this._user.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.isSubscribed = result;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.isSubscribed = false;
          this.detectChanges();
        },
      });
  }

  public subscribe() {
    if (this.isMe) {
      return;
    }
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {
        this.store.dispatch(subscribeToUser({ user_id: this._user.id }));
      });
    } else {
      this.store.dispatch(subscribeToUser({ user_id: this._user.id }));
    }
  }

  public unsubscribe() {
    if (this.isMe) {
      return;
    }
    this.store.dispatch(unsubscribeFromUser({ user_id: this._user.id }));
  }

  public onOpenComplainForm() {
    if (this.authService.isAuthenticated) {
      this.openComplainForm = true;
    } else {
      this.authService.showAuthModal(() => {
        this.openComplainForm = true;
        this.detectChanges();
      });
    }
  }

  public onCloseComplainForm() {
    this.openComplainForm = false;
    this.detectChanges();
  }

  public onOpenChat() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {
        this.chatService.openPrivateChat(this._user?.id);
      });
    } else {
      this.chatService.openPrivateChat(this._user?.id);
    }
  }

  public onToggleFavorite() {
    const action = this.isFavorite
      ? this.accountBookmarkService.removeBookmark(this._user.id, EntityTypeEnum.account)
      : this.accountBookmarkService.addBookmark(this._user.id, EntityTypeEnum.account);

    action.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isFavorite = !this.isFavorite;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.isFavorite = false;
        this.detectChanges();
      },
    });
  }

  public onToggleSubscribe() {
    if (this.isSubscribed) {
      this.store.dispatch(unsubscribeFromUser({ user_id: this._user.id }));
    } else {
      if (!this.authService.isAuthenticated) {
        this.authService.showAuthModal(() => {
          this.store.dispatch(subscribeToUser({ user_id: this._user.id }));
        });
      } else {
        this.store.dispatch(subscribeToUser({ user_id: this._user.id }));
      }
    }
    this.openActionsPopup = false;
  }

  public onOpenActionsPopup() {
    this.openActionsPopup = !this.openActionsPopup;
    this.detectChanges();
  }

  public languagesTransform(languages: string[]) {
    return languages.map((_l) => ({
      title: _l,
      native: false,
    }));
  }

  public setActivePage(page: string): void {
    if (this.activePage === page) {
      return;
    }
    this.clearInterval(this.loadedTimer);
    const startPage = this.isTeacher ? 'teaching' : 'about';
    this.loaded = this.activePage === startPage;
    this.activePage = page;
    this.detectChanges();
  }

  public afterLoaded(e) {
    this.clearInterval(this.loadedTimer);
    this.loaded = true;
    this.detectChanges();
  }

  private parseSubjectIdFromUrl(url: string): string {
    const lastUrlSegment = url.split('/').pop();
    const findSubject = this.teachingSubjects?.find((subject) => Number(subject.id) === Number(lastUrlSegment));
    if (!findSubject) {
      this.onChangeSubject(String(first(this.teachingSubjects)?.id));
      return String(first(this.teachingSubjects)?.id);
    } else {
      return this.router.url.split('/').pop();
    }
  }

  public onOpenSpeakLanguages() {
    this.openSpeakLanguagesTooltip = true;
  }

  public onCloseSpeakLanguages() {
    this.openSpeakLanguagesTooltip = false;
  }

  public onOpenTeachingSubjects() {
    this.openTeachingSubjectsTooltip = true;
  }

  public onCloseTeachingSubjects() {
    this.openTeachingSubjectsTooltip = false;
  }

  public onChangeSubject(id: string) {
    this.subjectId = this.selectedSubjectId = id;
    this.setActivePage(this.isTeacher ? 'teaching' : 'about');
    this.detectChanges();
  }

  public closeAllModals(e: boolean) {
    this.isCloseAllModals.emit(true);
  }

  public get isAvailableStatistics() {
    return this.features.isAvailable(FeatureEnum.statistics);
  }

  public get isAuthUser() {
    return this.authService.isAuthenticated;
  }

  public onNavigateToSubject(subject: BaseSubject) {
    if (this.subjectId === subject.id.toString()) {
      return;
    }

    const pagesForRedirect = ['collections', 'publications', 'schools', 'feedback'];
    const currentPage = this.location.path().split('/')[3];
    const profileUrl = AccountService.accountRoute(this._user).join('/');
    this.subjectId = this.selectedSubjectId = subject.id.toString();
    this.setActivePage(this.isTeacher ? 'teaching' : 'about');
    this.location.replaceState(
      profileUrl + '/' + currentPage + (pagesForRedirect.indexOf(currentPage) === -1 ? '/' + subject.id : ''),
    );
    this.disciplineChanged = new Date().getTime().toString();
    this.detectChanges();
  }

  public trackByFn(index) {
    return index;
  }
}
